import React, { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Activity, AlertTriangle, Fish, Gauge } from "lucide-react";
import { useLocation } from "react-router-dom";

type RigAlert = {
  basis: "personal-history" | "community";
  title: string;
  message: string;
  benchmark_pct: number;
  personal_pct: number;
};

type RigMetric = {
  key: string;
  rig: string;
  landed: number;
  aborted_takes: number;
  take_events: number;
  hooking_conversion_pct: number;
  adjusted_conversion_pct: number;
  exposure_hours: number | null;
  takes_per_24_rod_hours: number | null;
  evidence: string;
  community_benchmark: null | {
    take_events: number;
    hooking_conversion_pct: number;
    angler_count: number;
  };
  recent: null | {
    take_events: number;
    hooking_conversion_pct: number;
    previous_take_events: number;
    previous_conversion_pct: number;
  };
  alert: RigAlert | null;
};

type RigIntelligence = {
  rigs: RigMetric[];
  active_alert: (RigAlert & { rig: string; rods: number[] }) | null;
  broad_alert: null | { title: string; message: string; rig_count: number };
  methodology?: {
    formula?: string;
    recommendation_note?: string;
  };
};

function pctLabel(value: number) {
  return `${Number(value || 0).toFixed(1)}%`;
}

export function RigIntelligenceSurface({ embedded = false }: { embedded?: boolean } = {}) {
  const location = useLocation();
  const onStats = location.pathname === "/stats";
  const onSession = /^\/sessions\/\d+/.test(location.pathname);
  const relevant = onStats || onSession;
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    setDismissed(false);
  }, [location.pathname]);

  const query = useQuery<RigIntelligence>({
    queryKey: ["catchcompass_rig_intelligence"],
    queryFn: () =>
      fetch("/app-api/rig-intelligence", {
        credentials: "include",
        cache: "no-store",
      }).then(async (response) => {
        if (!response.ok) throw new Error("Rig intelligence unavailable");
        return response.json();
      }),
    enabled: relevant,
    staleTime: 60_000,
    refetchInterval: onSession ? 60_000 : false,
  });

  const rigs = useMemo(() => query.data?.rigs ?? [], [query.data]);

  if (!relevant || query.isLoading || query.error) return null;

  if (onSession) {
    const alert = query.data?.active_alert;
    if (!alert || dismissed) return null;
    const rodText = alert.rods?.length
      ? `Rod${alert.rods.length === 1 ? "" : "s"} ${alert.rods.join(", ")} · `
      : "";
    return (
      <div className="mx-auto max-w-[1200px] px-4 pt-4">
        <div
          className="rounded-xl border border-warning/45 bg-warning-weak p-4"
          role="alert"
        >
          <div className="flex items-start justify-between gap-3">
            <div className="flex min-w-0 gap-3">
              <AlertTriangle className="mt-0.5 size-5 shrink-0 text-warning" />
              <div className="min-w-0">
                <p className="text-sm font-semibold text-primary">Check your rig</p>
                <p className="mt-1 text-xs font-medium text-warning">
                  {rodText}{alert.rig}
                </p>
                <p className="mt-2 text-sm leading-6 text-secondary">
                  {alert.message}
                </p>
              </div>
            </div>
            <button
              type="button"
              className="shrink-0 text-xs font-semibold text-secondary hover:text-primary"
              onClick={() => setDismissed(true)}
              aria-label="Dismiss rig check"
            >
              Dismiss
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!onStats || rigs.length === 0) return null;

  const personalAlertCount = rigs.filter(
    (metric) => metric.alert?.basis === "personal-history",
  ).length;

  const statsContent = (
    <section className="space-y-4 rounded-2xl border border-border bg-raised p-4 sm:p-5">
      <div className="flex items-start gap-3">
        <Gauge className="mt-0.5 size-5 shrink-0 text-accent" />
        <div>
          <h2 className="text-lg font-semibold text-primary">Rig conversion</h2>
          <p className="mt-1 text-sm leading-6 text-secondary">
            CatchCompass separates generating a take from converting it.
            Hooking conversion is landed fish ÷ (landed fish + aborted takes).
            This Stats view uses only your own fishing evidence, and small samples
            are deliberately treated as early evidence.
          </p>
        </div>
      </div>

      {personalAlertCount >= 2 ? (
        <div className="rounded-xl border border-warning/40 bg-warning-weak p-4">
          <p className="text-sm font-semibold text-primary">
            Conversion is down across several rigs
          </p>
          <p className="mt-1 text-sm leading-6 text-secondary">
            More than one rig is converting recent takes below your own earlier
            recorded rate. Check hooks, terminal tackle and presentation before
            blaming one specific rig pattern.
          </p>
        </div>
      ) : null}

      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
        {rigs.slice(0, 9).map((metric) => (
          <article
            key={metric.key}
            className="rounded-xl border border-border bg-background/45 p-4"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <h3 className="truncate text-base font-semibold text-primary">
                  {metric.rig}
                </h3>
                <p className="mt-1 text-xs text-tertiary">{metric.evidence}</p>
              </div>
              <span className="shrink-0 rounded-full bg-inset px-2 py-1 text-xs font-semibold text-primary">
                {pctLabel(metric.hooking_conversion_pct)}
              </span>
            </div>

            <div className="mt-4 grid grid-cols-3 gap-2">
              <MiniMetric
                icon={<Fish />}
                label="Landed"
                value={metric.landed}
              />
              <MiniMetric
                icon={<AlertTriangle />}
                label="Aborted"
                value={metric.aborted_takes}
              />
              <MiniMetric
                icon={<Activity />}
                label="Takes"
                value={metric.take_events}
              />
            </div>

            <div className="mt-3 space-y-1 text-xs leading-5 text-secondary">
              {metric.takes_per_24_rod_hours != null ? (
                <p>
                  Take generation: {metric.takes_per_24_rod_hours.toFixed(2)} per
                  24 rod-hours
                </p>
              ) : null}
              {metric.recent ? (
                <p>
                  Recent: {pctLabel(metric.recent.hooking_conversion_pct)} · your
                  earlier rate: {pctLabel(metric.recent.previous_conversion_pct)}
                </p>
              ) : null}
            </div>

            {metric.alert?.basis === "personal-history" ? (
              <div className="mt-3 rounded-lg border border-warning/40 bg-warning-weak p-3">
                <p className="text-xs font-semibold text-primary">Check your rig</p>
                <p className="mt-1 text-xs leading-5 text-secondary">
                  {metric.alert.message}
                </p>
              </div>
            ) : null}
          </article>
        ))}
      </div>

      <p className="text-xs leading-5 text-tertiary">
        Aborted takes reduce conversion confidence, but they are not treated as
        the same thing as a quiet blank. A rig can still be good at generating
        feeding responses while needing attention to its hooking mechanics.
      </p>
    </section>
  );

  if (embedded) return statsContent;

  return (
    <div className="mx-auto max-w-[1200px] px-4 pt-6 md:pt-8">
      {statsContent}
    </div>
  );
}

function MiniMetric({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: string | number;
}) {
  return (
    <div className="rounded-lg bg-inset p-2.5">
      <div className="flex items-center gap-1.5 text-tertiary [&_svg]:size-3.5">
        {icon}
        <span className="text-[10px] uppercase tracking-wide">{label}</span>
      </div>
      <p className="mt-1 text-sm font-semibold text-primary">{value}</p>
    </div>
  );
}
